import {
  Box,
  Grid,
  Button,
  Card,
  makeStyles,
  MenuItem,
  Select,
} from '@material-ui/core';
import React from 'react';
import { Helmet } from 'react-helmet';
import CreateTodo from './CreateTodo';
import {
  UPDATE_TODO,
  UPSERT_TODO,
  UPDATE_TASK_STATUS,
  DELETE_TASK,
} from 'graphql/todos';
import { useMutation } from 'react-apollo';
import useSnack from 'hooks/useSnack';
import { API } from 'aws-amplify';
import Search from 'components/common/Search';
import TodoList from './components/TodoList';
import { DragDropContext } from 'react-beautiful-dnd';
import ReferralPopup from 'modules/referral/components/ReferralPopup';
import { useSelector } from 'react-redux';

const useStyles = makeStyles((theme) => ({
  title: {
    marginLeft: theme.spacing(1),
  },
  icon: {
    color: theme.palette.text.secondary,
  },

  noteicon: {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(1),
  },
  card: {
    '& a': {
      color: theme.palette.primary.main,
    },
    '& .MuiSelect-icon': {
      color: '#B4BAC8',
    },
    'marginBottom': theme.spacing(4),
  },
  select: {
    paddingTop: 0,
    paddingBottom: 4,
    fontSize: '14px',
    borderRadius: 4,
  },
  item: {
    fontSize: 'inherit',
  },
  label: {
    paddingBottom: theme.spacing(1),
    color: '#D2D7E3',
    fontWeight: 600,
    textTransform: 'uppercase',
  },
  selectStatus: {
    paddingTop: 4,
    paddingBottom: 4,
    paddingLeft: 8,
    fontSize: '12px',
    borderRadius: 4,
  },
  info: {
    flexWrap: 'wrap',
  },
  btns: {
    '& button': {
      borderRadius: 30,
      margin: theme.spacing(0.5),
    },
  },
  filterModal: {
    marginBottom: 100,
  },
  tabs: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  track: {
    background: theme.palette.text.secondary,
  },
  board: {
    [theme.breakpoints.down('md')]: {
      flexWrap: 'nowrap',
      overflowX: 'scroll',
    },
  },
}));

export const PRIORITY = [
  { label: 'Low', value: 'low' },
  { label: 'Medium', value: 'medium' },
  { label: 'High', value: 'high' },
];

const LISTS = [
  { label: 'Todo', status: 'pending' },
  { label: 'In Progress', status: 'doing' },
  { label: 'Done', status: 'done' },
  { label: 'Won’t do', status: 'closed' },
];

const TODOS = () => {
  const classes = useStyles();
  const user = useSelector((state) => state.user.user);
  const [showForm, toggleForm] = React.useState(false);
  const [task, setTask] = React.useState(null);
  const [search, setSearch] = React.useState('');
  const [inputVal, setInputVal] = React.useState('');
  const [upsertTodoTask] = useMutation(UPSERT_TODO);
  const [updateTodo] = useMutation(UPDATE_TODO);
  const [updateStatus] = useMutation(UPDATE_TASK_STATUS);
  const [deleteTask] = useMutation(DELETE_TASK);
  const snack = useSnack();

  const handlePriorityChange = (e) => {
    setSearch(e.target.value);
    setInputVal('');
  };

  const onSave = async (data) => {
    if (data.id) {
      await updateTodo({
        variables: {
          id: data.id,
          data
        }
      })
    } else {
      await upsertTodoTask({ variables: { data } });
    }
  };

  const handleCreateTask = () => {
    setTask(null);
    toggleForm(true);
  };

  const onSearch = () => {
    if (!inputVal) {
      setSearch();
    } else {
      setSearch(inputVal);
    }
  };

  const onEdit = (task) => {
    setTask(task);
    toggleForm(true);
  };

  const markDone = async (task) => {
    await updateStatus({ variables: { id: task.id } }).then(
      () => {
        snack.success('Marked as Done.');
      },
      (e) => snack.error(e.message),
    );
  };

  const onDelete = async (task) => {
    await deleteTask({ variables: { id: task.id } }).then(
      () => {
        snack.success('Task Deleted.');
      },
      (e) => snack.error(e.message),
    );
  };

  const sendNotification = async (data) => {
    try {
      return await API.post('api', '/users/send-notification', {
        body: {
          data,
          skipTemplates: true,
          topic: 'TODO_CREATED',
        },
      }).then((res) => {
        snack.success('Notification Sent');
      });
    } catch (e) {
      snack.error(e.message);
    }
  };

  const onDragEnd = (result) => {
    const { source, destination, draggableId } = result;
    // dropped nowhere
    if (!destination) {
      return;
    }

    if (source.droppableId === destination.droppableId) {
      // reordering
      if (source.index === destination.index) {
        return;
      }
      // TODO update the order
    } else {
      const id = Number(draggableId);
      return updateTodo({
        variables: {
          id,
          data: {
            status: destination.droppableId,
          },
        },
      });
    }
  };

  return (
    <>
      <Helmet title="TODOS" />
      <Card className={classes.card}>
        <Box p={2}>
          <Box
            display="flex"
            className={classes.info}
            justifyContent="space-between"
            alignItems="center"
          >
            <div>
              <Search
                placeholder="Search..."
                inputVal={inputVal}
                onChange={setInputVal}
                onSearch={onSearch}
              />
            </div>
            <Select
              displayEmpty
              classes={{
                select: classes.select,
              }}
              id="priority"
              name="priority"
              value={search}
              placeholder="Priority"
              onChange={handlePriorityChange}
              renderValue={(val) =>
                PRIORITY.find((s) => s.value === val)?.label || 'Select'
              }
            >
              <MenuItem
                classes={{ root: classes.item }}
                key="all"
                value=""
              >
                All
              </MenuItem>
              {PRIORITY.map((i) => (
                <MenuItem
                  classes={{ root: classes.item }}
                  key={i.value}
                  value={i.value}
                >
                  {i.label}
                </MenuItem>
              ))}
            </Select>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCreateTask}
            >
              Create Task
            </Button>
          </Box>
        </Box>
      </Card>
      <DragDropContext onDragEnd={onDragEnd}>
        <Grid container spacing={2} className={classes.board}>
          {LISTS.map((i) => (
            <Grid item xs={12} md={3} key={i.status}>
              <TodoList
                status={i.status}
                label={i.label}
                sendNotification={sendNotification}
                onDelete={onDelete}
                onEdit={onEdit}
                markDone={markDone}
                query={search ? `%${search}%` : undefined}
              />
            </Grid>
          ))}
        </Grid>
      </DragDropContext>
      {(user.type === 'lead_coach' || user.type === 'head_coach') && <ReferralPopup />}
      {showForm && (
        <CreateTodo
          task={task}
          onSave={onSave}
          onClose={() => toggleForm(false)}
        />
      )}
    </>
  );
};

export default TODOS;
